import React, { useState, useEffect, useMemo } from "react";
import {
  Select,
  MenuItem,
  Switch,
  Button,
  Paper,
  Typography,
  Box,
  Stack,
  styled,
  useTheme,
  Checkbox,
} from "@mui/material";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DroppableProvided,
  DraggableProvided,
} from "@hello-pangea/dnd";
import reset from "../assets/icons/Reset.svg";
import arrow from "../assets/icons/Arrow.svg";
import toggleVector from "../assets/icons/toggleVector.svg";
import DragIcon from "../assets/icons/DragIcon.svg";
import CustomDropDownInput from "../common/components/customDropDownInput";
import arrowUp from "packages/novous_web/src/assets/icons/ArrowBlack.svg";
import arrowDown from "packages/novous_web/src/assets/icons/ArrowDownBlack.svg";
interface StatOption {
  label: string;
  value: string;
}

interface Section {
  id: string;
  name: string;
  active: boolean;
}

interface OptionType {
  value: string;
  label: string;
  disabled?: boolean;
}

// Update the SectionsConfig interface according to defaultSections
interface SectionsConfig {
  "distance-wise-fleet-utilization-graph": boolean;
  "time-wise-fleet-utilization-graph": boolean;
  "day-wise-km-running-graph": boolean;
  "average-speed-graph": boolean;
  "fleet-safety-score": boolean;
  "day-wise-warnings": boolean;
  [key: string]: boolean;
}

interface ManageSectionLayoutProps {
  onClose: () => void;
  sectionsConfig: SectionsConfig;
  setSectionsConfig: React.Dispatch<React.SetStateAction<SectionsConfig>>;
  onToggleSection: (sectionId: keyof SectionsConfig) => void;
  setTilesObj?: React.Dispatch<React.SetStateAction<TilesObjType>>;
  tilesObj?: TilesObjType;
}

const defaultStats = {
  stat1: "total_vehicles",
  stat2: "average_mileage",
  stat3: "average_km_running",
  stat4: "average_idling_time",
};

const defaultSections: Section[] = [
  {
    id: "distance-wise-fleet-utilization-graph",
    name: "Distance Wise Fleet Utilization Graph",
    active: true,
  },
  {
    id: "time-wise-fleet-utilization-graph",
    name: "Time Wise Fleet Utilization Graph",
    active: true,
  },
  {
    id: "day-wise-km-running-graph",
    name: "Day Wise KM Running Graph",
    active: true,
  },
  {
    id: "average-speed-graph",
    name: "Average Speed Graph",
    active: true,
  },
  {
    id: "fleet-safety-score",
    name: "Fleet Safety Score",
    active: true,
  },
  {
    id: "day-wise-warnings",
    name: "Day Wise Warnings",
    active: true,
  },
];

const SectionContainer = styled(Paper)(({ theme }) => ({
  width: 896,
  overflowY: "auto", // Enables vertical scrolling if content exceeds the max height
  borderRadius: theme.shape.borderRadius * 4,
  padding: theme.spacing(4),
  boxShadow: theme.shadows[6],
  backgroundColor: theme.palette.background.paper,
  border: "1px solid #F0EFF8",
}));

const SectionItemWrapper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(1),
  backgroundColor:
    theme.palette.mode === "light"
      ? theme.palette.grey[100]
      : theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(["background-color", "box-shadow"]),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatSelect = styled(Select)(({ theme }) => ({
  height: 48,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
    borderWidth: 1,
  },
}));


type TilesObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};


const VehicleManageSectionLayout: React.FC<ManageSectionLayoutProps> = ({
  onClose,
  sectionsConfig,
  setSectionsConfig,
  onToggleSection,
  setTilesObj,
  tilesObj,
}) => {
  const toggleSection = (sectionId: keyof SectionsConfig) => {
    setSectionsConfig((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
    console.log(
      `Section "${sectionId}" toggled to ${!sectionsConfig[sectionId]}`
    );
  };

  const theme = useTheme();
  const [stats, setStats] = useState(defaultStats);
  const [activeSections, setActiveSections] =
    useState<Section[]>(defaultSections);
  const [hiddenSections, setHiddenSections] = useState<Section[]>([]);
  const [isResetAnimating, setIsResetAnimating] = useState(false);
  const [showStats, setShowStats] = useState(true);
  const [showSectionsLayout, setShowSectionsLayout] = useState(true);
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [tileSelections, setTileSelections] = useState<Record<string, string>>({});

  const statsOption = useMemo(() => {
    if (tilesObj) {
      const tiles = Object.keys(tilesObj).map((item: string) => ({
        tileCode: tilesObj[item].name,
        tileName: tilesObj[item].name,
        label: tilesObj[item].name,
      }));
      // Add the "None" option.
      tiles.push({
        tileCode: 'none',
        tileName: "None",
        label: 'None',
      });
      return tiles;
    }
    return [];
  }, [tilesObj]);

  const toggleOverview = () => {
    setOverviewExpanded((prev) => !prev);
  };

  const handleTileChange = (tileKey: string, selectedValue: string) => {
    setTileSelections((prev) => ({
      ...prev,
      [tileKey]: selectedValue,
    }));
    console.log(`Tile ${tileKey} changed to:`, selectedValue);
  };

  useEffect(() => {
    if (tilesObj) {
      const initialSelections = Object.keys(tilesObj).reduce((acc, key) => {
        acc[key] = tilesObj[key].name;
        return acc;
      }, {} as Record<string, string>);

      console.log(initialSelections, '   inti');

      setTileSelections(initialSelections);
    }
  }, [tilesObj]);


  const statOptions: StatOption[] = [
    { label: "Total vehicles", value: "total_vehicles" },
    { label: "Average Mileage", value: "average_mileage" },
    { label: "Average KM Running", value: "average_km_running" },
    { label: "Average Idling Time", value: "average_idling_time" },
  ];

  const handleStatChange = (statKey: keyof typeof stats) => (event: any) => {
    setStats((prev) => ({
      ...prev,
      [statKey]: event.target.value,
    }));
  };

  const handleToggleActive = (section: Section) => {
    setActiveSections((prev) => prev.filter((s) => s.id !== section.id));
    setHiddenSections((prev) => [...prev, { ...section, active: false }]);
  };

  const handleToggleHidden = (section: Section) => {
    setHiddenSections((prev) => prev.filter((s) => s.id !== section.id));
    setActiveSections((prev) => [...prev, { ...section, active: true }]);
  };

  const handleSave = () => {
    const newConfig = {} as SectionsConfig;
    [...activeSections, ...hiddenSections].forEach((section) => {
      newConfig[section.id] = section.active;
    });
    let tempTileObj = { ...tilesObj };
    const updatedTileObj = Object.keys(tempTileObj).reduce((acc, key) => {
      acc[key] = {
        ...tempTileObj[key],
        status: Object.values(tileSelections).includes(tempTileObj[key].name) // Check if the name exists in tileSelection values
      };
      return acc;
    }, {} as typeof tempTileObj);


    console.log(updatedTileObj, "tilesObj -------------");

    if (setTilesObj)
      setTilesObj(updatedTileObj)
    setSectionsConfig(newConfig);
    onClose();
  };

  useEffect(() => {
    const updatedSections = defaultSections.map((section) => ({
      ...section,
      active: sectionsConfig[section.id],
    }));
    const updatedActiveSections = updatedSections.filter(
      (section) => section.active
    );
    const updatedHiddenSections = updatedSections.filter(
      (section) => !section.active
    );
    setActiveSections(updatedActiveSections);
    setHiddenSections(updatedHiddenSections);
  }, [sectionsConfig]);

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(activeSections);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    setActiveSections(items);
  };

  const handleReset = () => {
    setStats(defaultStats);
    setActiveSections(defaultSections);
    setHiddenSections([]);
    setIsResetAnimating(true);
    setTimeout(() => setIsResetAnimating(false), 500);
  };

  const renderSectionPair = (
    section1: Section,
    section2: Section | undefined,
    pairIndex: number
  ) => (
    <Stack direction="row" spacing={2} key={section1.id} sx={{ width: "100%" }}>
      <Box sx={{ flex: 1 }}>
        <SectionItem
          section={section1}
          index={pairIndex * 2}
          onToggle={() => handleToggleActive(section1)}
        />
      </Box>
      {section2 && (
        <Box sx={{ flex: 1 }}>
          <SectionItem
            section={section2}
            index={pairIndex * 2 + 1}
            onToggle={() => handleToggleActive(section2)}
          />
        </Box>
      )}
    </Stack>
  );

  return (
    <SectionContainer>
      <Stack spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h5" fontWeight="medium">
            Customize Your Dashboard
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleReset}
            startIcon={
              <img
                src={reset}
                alt="Reset"
                style={{
                  width: 20,
                  height: 20,
                  transition: theme.transitions.create("transform"),
                  transform: isResetAnimating ? "rotate(180deg)" : "none",
                }}
              />
            }
            sx={{
              textTransform: "none",
              py: 1,
              px: 2,
              borderRadius: 2,
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Reset
          </Button>
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Choose which sections to display and arrange them the way you like.
        </Typography>
        <div className="flex flex-col mt-2 gap-2">
          <div
            className="flex justify-between w-full items-center cursor-pointer"
            onClick={toggleOverview}
          >
            <Typography variant="body1">Top Overview Stats</Typography>
            <img
              src={overviewExpanded ? arrowDown : arrowUp}
              width={24}
              height={24}
              alt={overviewExpanded ? 'collapse' : 'expand'}
            />
          </div>
          {overviewExpanded && (
            <div className="flex gap-2">
              {tilesObj &&
                Object.keys(tilesObj).map((tileKey: string) => {
                  // Determine values selected in other dropdowns.
                  const alreadySelected = Object.keys(tileSelections)
                    .filter(key => key !== tileKey)
                    .map(key => tileSelections[key])
                    .filter(val => val !== "" && val !== "none");

                  // Filter statsOption and map to OptionType.
                  const filteredOptions: OptionType[] = statsOption
                    .filter(option => !alreadySelected.includes(option.tileCode))
                    .map(option => ({ value: option.tileCode, label: option.label }));

                  return (
                    <CustomDropDownInput
                      key={tileKey}
                      label={tileKey}
                      isMandatory={true}
                      options={filteredOptions}
                      value={tileSelections[tileKey] || ""}
                      onChange={(selectedValue: string) => handleTileChange(tileKey, selectedValue)}
                      placeholder="Select tile"
                    />
                  );
                })
              }
            </div>
          )}
        </div>
        <Stack spacing={3}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="medium">
              Sections & Layout
            </Typography>
            <img
              src={toggleVector}
              alt="Toggle"
              onClick={() => setShowSectionsLayout((prev) => !prev)}
              style={{
                width: 16,
                height: 9,
                cursor: "pointer",
                transition: "transform 0.3s",
                transform: showSectionsLayout
                  ? "rotate(180deg)"
                  : "rotate(0deg)",
                filter: theme.palette.mode === "dark" ? "invert(1)" : "none",
              }}
            />
          </Stack>
          {showSectionsLayout && (
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sections">
                {(provided: DroppableProvided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {Array.from({
                      length: Math.ceil(activeSections.length / 2),
                    }).map((_, idx) =>
                      renderSectionPair(
                        activeSections[idx * 2],
                        activeSections[idx * 2 + 1],
                        idx
                      )
                    )}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </Stack>
        {hiddenSections.length > 0 && (
          <Stack spacing={3}>
            <Typography variant="h6" fontWeight="medium">
              Hidden Sections
            </Typography>
            <Stack spacing={1}>
              {hiddenSections.map((section) => (
                <HiddenSectionItem
                  key={section.id}
                  section={section}
                  onToggle={() => handleToggleHidden(section)}
                />
              ))}
            </Stack>
          </Stack>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mt: 4 }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={onClose}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "medium",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: "medium",
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </SectionContainer>
  );
};

interface SectionItemProps {
  section: Section;
  index: number;
  onToggle: () => void;
}

const SectionItem: React.FC<SectionItemProps> = ({
  section,
  index,
  onToggle,
}) => {
  const theme = useTheme();

  return (
    <Draggable draggableId={section.id} index={index}>
      {(provided: DraggableProvided) => (
        <SectionItemWrapper
          ref={provided.innerRef}
          {...provided.draggableProps}
          elevation={0}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box {...provided.dragHandleProps} sx={{ cursor: "grab" }}>
              <img
                src={DragIcon}
                alt="Drag handle"
                style={{
                  width: 24,
                  height: 24,
                  filter: theme.palette.mode === "dark" ? "none" : "invert(1)",
                  color: theme.palette.mode === "dark" ? "#5547DD" : "inherit",
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 600,
              }}
            >
              {section.name}
            </Typography>
          </Stack>
          <Switch
            checked={section.active}
            onChange={(_event, checked) => {
              console.log(`Section "${section.id}" toggled to ${checked}`);
              onToggle();
            }}
            color="primary"
            sx={{
              "& .MuiSwitch-switchBase": {
                color: theme.palette.grey[400],
              },
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: theme.palette.primary.main,
              },
            }}
          />
        </SectionItemWrapper>
      )}
    </Draggable>
  );
};

const HiddenSectionItem: React.FC<Omit<SectionItemProps, "index">> = ({
  section,
  onToggle,
}) => {
  const theme = useTheme();

  return (
    <SectionItemWrapper elevation={0}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <img
          src={DragIcon}
          alt="Icon"
          style={{
            width: 24,
            height: 24,
            filter: theme.palette.mode === "dark" ? "none" : "invert(1)",
            color: theme.palette.mode === "dark" ? "#5547DD" : "inherit",
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.body2.fontSize,
            fontWeight: 600,
          }}
        >
          {section.name}
        </Typography>
      </Stack>
      <Switch
        checked={section.active}
        onChange={(_event, checked) => {
          console.log(`Hidden Section "${section.id}" toggled to ${checked}`);
          onToggle();
        }}
        color="primary"
        sx={{
          "& .MuiSwitch-switchBase": {
            color: theme.palette.grey[400],
          },
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: theme.palette.primary.main,
          },
        }}
      />
    </SectionItemWrapper>
  );
};

export default VehicleManageSectionLayout;
